/**
* EventBatch.ts
* @author Nev Wylie (newylie)
* @copyright Microsoft 2020
*/
import dynamicProto from '@microsoft/dynamicproto-js';
import { CoreUtils } from '@ms/1ds-core-js';
/**
* This class defines a "batch" events related to a specific iKey, it is used by the PostChannel and HttpManager
* to collect and transfer ownership of events without duplicating them in-memory. This reduces the previous
* array duplication and shared ownership issues that occurred due to race conditions caused by the async nature
* of sending requests.
*/
var EventBatch = /** @class */ (function () {
    /**
     * Private constructor so that caller is forced to use the static create method.
     * @param iKey - The iKey to associate with the events (not validated)
     * @param theEvents - The optional collection of events to assign to this batch - defaults to an empty array.
     */
    function EventBatch(iKey, theEvents) {
        var events = theEvents ? [].concat(theEvents) : [];
        var isNullOrUndefined = CoreUtils.isNullOrUndefined;
        dynamicProto(EventBatch, this, function (_self) {
            _self.iKey = function () {
                return iKey;
            };
            _self.count = function () {
                return events.length;
            };
            _self.events = function () {
                return events;
            };
            _self.addEvents = function (theEvents, append) {
                if (append === void 0) { append = true; }
                if (theEvents && theEvents.length > 0) {
                    if (append) {
                        events = events.concat(theEvents);
                    }
                    else {
                        events = theEvents.concat(events);
                    }
                    return theEvents.length;
                }
                return 0;
            };
            _self.split = function (fromEvent, numEvents) {
                // Create a new batch with the same iKey
                var newBatch = new EventBatch(iKey);
                if (fromEvent < events.length) {
                    var cnt = events.length - fromEvent;
                    if (!isNullOrUndefined(numEvents)) {
                        cnt = numEvents < cnt ? numEvents : cnt;
                    }
                    newBatch.addEvents(events.splice(fromEvent, cnt));
                }
                return newBatch;
            };
        });
    }
    /**
     * Creates a new Event Batch object
     * @param iKey The iKey associated with this batch of events
     */
    EventBatch.create = function (iKey, theEvents) {
        return new EventBatch(iKey, theEvents);
    };
    /**
     * Returns the iKey associated with this batch of events
     */
    EventBatch.prototype.iKey = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Returns the number of events contained in the batch
     */
    EventBatch.prototype.count = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return 0;
    };
    EventBatch.prototype.events = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     * Add all of the events to the current batch, if the max number of events would be exceeded then no
     * events are added.
     * @param theEvents - The events that needs to be batched.
     * @param append - True (default) add the event to the end of the queue otherwise prepend (used for re-queuing)
     * @returns The number of events added.
     */
    EventBatch.prototype.addEvents = function (theEvents, append) {
        if (append === void 0) { append = true; }
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return 0;
    };
    /**
     * Split this batch into 2 with any events > fromEvent returned in the new batch and all other
     * events are kept in the current batch.
     * @param fromEvent The first event to remove from the current batch.
     * @param numEvents The number of events to be removed from the current batch and returned in the new one. Defaults to all trailing events
     */
    EventBatch.prototype.split = function (fromEvent, numEvents) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    return EventBatch;
}());
export { EventBatch };
//# sourceMappingURL=EventBatch.js.map